import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { DocumentsView } from '@/features/ged';
import {
  ArrowLeft,
  User,
  FileText,
  Receipt,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Briefcase,
  Building2,
  CheckCircle,
  XCircle,
  Euro,
  Clock,
  Shield,
  Download,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useEmployeeDetail, useEmployeeContractsDetails } from '../hooks';
import { CIVILITE_MAP, EmployeeDetails } from '../types';
import { useEmployeeVault } from '@/hooks/useVault';
import { CreateEmployeeVaultModal } from '@/features/vault/components';
import { DownloadBulletinsDrawer } from '@/features/domains/components/DownloadBulletinsDrawer';

const formatDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('fr-FR');
};

const formatAddress = (employee?: EmployeeDetails | null) => {
  const address = employee?.adresse;
  if (!address) {
    return null;
  }

  const lineParts = [address.ligne1, address.ligne2].filter(
    Boolean
  ) as string[];
  const cityLine = [address.codePostal, address.ville]
    .filter(Boolean)
    .join(' ');
  const parts = [...lineParts, cityLine, address.pays].filter(
    Boolean
  ) as string[];

  return parts.join(', ');
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined) {
    return null;
  }

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatHours = (value?: number | null) => {
  if (value === null || value === undefined) {
    return null;
  }

  return `${value}h`;
};

const EMPTY_CONTRACTS: EmployeeDetails['contrats'] = [];

export function EmployeeDetailPage() {
  const { t } = useTranslation();
  const { folderUuid, employeeUuid } = useParams();
  const navigate = useNavigate();
  const [isCreateVaultModalOpen, setIsCreateVaultModalOpen] = useState(false);
  const [isDownloadBulletinsDrawerOpen, setIsDownloadBulletinsDrawerOpen] = useState(false);

  const {
    data: employee,
    isLoading,
    error,
  } = useEmployeeDetail(folderUuid, employeeUuid);

  const {
    data: employeeVault,
    isLoading: isLoadingVault,
  } = useEmployeeVault(employeeUuid || '', !!employeeUuid);

  const contracts = employee?.contrats ?? EMPTY_CONTRACTS;
  const contractIds = useMemo(
    () => contracts.map(contract => contract.uuid),
    [contracts]
  );
  const { data: contractDetails, isLoading: areContractDetailsLoading } =
    useEmployeeContractsDetails(folderUuid, contractIds);

  const contractDetailsMap = useMemo(() => {
    return new Map(contractDetails.map(detail => [detail.uuid, detail]));
  }, [contractDetails]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <LoadingSkeleton className="h-8 w-64" />
        <LoadingSkeleton className="h-12 w-full" />
        <LoadingSkeleton className="h-96" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/folders/${folderUuid}`)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-900">
              {t('employees.detail.title', 'Employee details')}
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-error-100 mb-4 rounded-full p-3">
            <XCircle className="text-error-600 h-6 w-6" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('common.error', 'Error')}
          </h3>
          <p className="text-sm text-neutral-600">
            {t('employees.detail.error', 'Unable to load employee details')}
          </p>
        </div>
      </div>
    );
  }

  const civility = employee.civilite
    ? CIVILITE_MAP[employee.civilite]
    : undefined;
  const lastName = employee.nomUsage ?? employee.nomNaissance ?? '';
  const firstName = employee.prenom ?? '';
  const fullName =
    [civility, [firstName, lastName].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(' ') || t('employees.unknownName', 'Name not provided');

  const birthDate = formatDate(employee.dateNaissance);
  const birthPlace = employee.communeNaissance
    ? `${employee.communeNaissance}${employee.departementNaissance ? ` (${employee.departementNaissance})` : ''}`
    : null;
  const address = formatAddress(employee);
  const iban = employee.banque?.iban ?? null;
  const bic = employee.banque?.bic ?? null;
  const wireTransfer =
    employee.banque?.virement === undefined ||
    employee.banque?.virement === null
      ? null
      : employee.banque.virement
        ? t('common.yes', 'Yes')
        : t('common.no', 'No');
  const totalDocuments = employee.metadata?.totalDocuments ?? 0;
  const totalBulletins = employee.metadata?.totalBulletins ?? 0;

  const currentContract =
    contracts.find(contract => contract.estCourant) ?? contracts[0];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/folders/${folderUuid}`)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="flex-1 space-y-1">
          <h1 className="text-2xl font-bold text-neutral-900">{fullName}</h1>
          <p className="text-sm text-neutral-600">
            {t('employees.matricule', 'Employee ID')}:{' '}
            {employee.matriculeSalarie ?? '—'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {employee.isActive ? (
            <>
              <CheckCircle className="text-success-500 h-5 w-5" />
              <span className="text-success-700 text-sm font-medium">
                {t('common.active', 'Active')}
              </span>
            </>
          ) : (
            <>
              <XCircle className="text-error-500 h-5 w-5" />
              <span className="text-error-700 text-sm font-medium">
                {t('common.inactive', 'Inactive')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="informations" className="w-full">
        <TabsList className="mb-6 w-full justify-start">
          <TabsTrigger value="informations" className="gap-2">
            <User className="h-4 w-4" />
            {t('employees.tabs.informations', 'Information')}
          </TabsTrigger>
          <TabsTrigger value="contracts" className="gap-2">
            <Briefcase className="h-4 w-4" />
            {t('employees.tabs.contract', 'Contracts')}
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            {t('employees.tabs.documents', 'Documents')}
          </TabsTrigger>
          <TabsTrigger value="bulletins" className="gap-2">
            <Receipt className="h-4 w-4" />
            {t('employees.tabs.bulletins', 'Payslips')}
          </TabsTrigger>
        </TabsList>

        {/* Informations */}
        <TabsContent value="informations" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-neutral-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-lg">
                  <User className="text-primary-600 h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    {t('employees.personalInfo', 'Personal information')}
                  </h2>
                </div>
              </div>

              <div className="space-y-3">
                <InfoField
                  label={t('employees.firstName', 'First name')}
                  value={firstName}
                />
                <InfoField
                  label={t('employees.lastName', 'Last name')}
                  value={lastName}
                />
                <InfoField
                  label={t('employees.civilite', 'Civility')}
                  value={civility}
                />
                <InfoField
                  label={t('employees.birthDate', 'Birth date')}
                  value={birthDate}
                  icon={<Calendar className="h-4 w-4" />}
                />
                <InfoField
                  label={t('employees.birthPlace', 'Birth place')}
                  value={birthPlace}
                  icon={<MapPin className="h-4 w-4" />}
                />
                <InfoField
                  label={t('employees.nationality', 'Nationality')}
                  value={employee.nationalite ?? null}
                />
                <InfoField
                  label={t('employees.ssn', 'Social security number')}
                  value={employee.numeroSecuriteSociale ?? null}
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border border-neutral-200 bg-white p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
                    <Mail className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t('employees.contact', 'Contact')}
                    </h2>
                  </div>
                </div>

                <div className="space-y-3">
                  <InfoField
                    label={t('employees.email', 'Email')}
                    value={employee.email ?? null}
                    icon={<Mail className="h-4 w-4" />}
                  />
                  <InfoField
                    label={t('employees.phone', 'Phone')}
                    value={employee.telephone ?? null}
                    icon={<Phone className="h-4 w-4" />}
                  />
                  <InfoField
                    label={t('employees.address', 'Address')}
                    value={address}
                    icon={<MapPin className="h-4 w-4" />}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-neutral-200 bg-white p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t('employees.banking', 'Banking')}
                    </h2>
                  </div>
                </div>

                <div className="space-y-3">
                  <InfoField label={t('employees.iban', 'IBAN')} value={iban} />
                  <InfoField label={t('employees.bic', 'BIC')} value={bic} />
                  <InfoField
                    label={t('employees.wireTransfer', 'Wire transfer')}
                    value={wireTransfer}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-neutral-200 bg-white p-4 text-center">
              <p className="text-2xl font-semibold text-neutral-900">
                {totalDocuments}
              </p>
              <p className="text-sm text-neutral-500">
                {t('employees.documents', 'Documents')}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4 text-center">
              <p className="text-2xl font-semibold text-neutral-900">
                {totalBulletins}
              </p>
              <p className="text-sm text-neutral-500">
                {t('employees.bulletins.title', 'Payslips')}
              </p>
            </div>
            {currentContract && (
              <div className="rounded-lg border border-neutral-200 bg-white p-4 text-center">
                <p className="text-xl font-semibold text-neutral-900">
                  {currentContract.establishmentNom ??
                    currentContract.codeEtablissement ??
                    '—'}
                </p>
                <p className="text-sm text-neutral-500">
                  {t('employees.contract.current', 'Current contract')}
                </p>
              </div>
            )}

            {/* Vault Card */}
            <div
              onClick={() => {
                if (employeeVault) {
                  navigate(`/vault/${employeeVault.uuid}`);
                } else {
                  setIsCreateVaultModalOpen(true);
                }
              }}
              className="group cursor-pointer rounded-lg border border-neutral-200 bg-white p-4 text-center transition-all hover:scale-105 hover:border-primary-300 hover:bg-primary-50 hover:shadow-md"
            >
              <div className="mb-2 flex justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 transition-colors group-hover:bg-primary-200">
                  <Shield className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <p className="text-sm font-semibold text-neutral-900">
                {isLoadingVault
                  ? t('vault.loading', 'Chargement...')
                  : employeeVault
                  ? t('vault.access', 'Coffre-Fort')
                  : t('vault.create', 'Créer Coffre')}
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                {employeeVault
                  ? t('vault.clickToAccess', 'Cliquez pour accéder')
                  : t('vault.clickToCreate', 'Documents personnels')}
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Contracts */}
        <TabsContent value="contracts">
          {contracts.length > 0 ? (
            <div className="space-y-4">
              {areContractDetailsLoading && (
                <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-600">
                  <Clock className="text-primary-500 h-4 w-4 animate-spin" />
                  {t(
                    'employees.contract.loading',
                    'Loading contract details...'
                  )}
                </div>
              )}
              {contracts.map(contract => {
                const startDate = formatDate(contract.dateDebut);
                const plannedEnd = formatDate(contract.dateFinPrevisionnelle);
                const effectiveEnd = formatDate(contract.dateFinEffective);
                const detail = contractDetailsMap.get(contract.uuid);
                const monthlySalary = formatCurrency(detail?.monthlySalary);
                const hourlySalary = formatCurrency(detail?.hourlySalary);
                const weeklyHours = formatHours(detail?.schedule?.weeklyHours);
                const monthlyHours = formatHours(
                  detail?.schedule?.monthlyHours
                );
                const terminationExitDate = formatDate(
                  detail?.termination?.exitDate ?? null
                );
                const terminationNotificationDate = formatDate(
                  detail?.termination?.notificationDate ?? null
                );
                const terminationLastWorkedDay = formatDate(
                  detail?.termination?.lastWorkedDay ?? null
                );

                return (
                  <div
                    key={contract.uuid}
                    className="rounded-lg border border-neutral-200 bg-white p-6"
                  >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-neutral-900">
                          {contract.establishmentNom ??
                            contract.codeEtablissement ??
                            t(
                              'employees.contract.unknownEstablishment',
                              'Unknown establishment'
                            )}
                        </h3>
                        <p className="text-sm text-neutral-500">
                          {t('employees.contract.number', 'Contract number')}:{' '}
                          {contract.numeroContrat ?? '—'}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${contract.isActif ? 'bg-success-100 text-success-700' : 'bg-neutral-100 text-neutral-600'}`}
                        >
                          {contract.isActif
                            ? t('employees.contract.statusActive', 'Active')
                            : t(
                                'employees.contract.statusInactive',
                                'Inactive'
                              )}
                        </span>
                        {contract.estCourant && (
                          <span className="bg-primary-100 text-primary-700 rounded-full px-3 py-1 text-xs font-medium">
                            {t('employees.contract.current', 'Current')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      <InfoField
                        label={t('employees.contract.startDate', 'Start date')}
                        value={startDate}
                        icon={<Calendar className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.plannedEnd',
                          'Planned end date'
                        )}
                        value={plannedEnd}
                        icon={<Calendar className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.effectiveEnd',
                          'Effective end date'
                        )}
                        value={effectiveEnd}
                        icon={<Calendar className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.establishmentCode',
                          'Establishment code'
                        )}
                        value={contract.codeEtablissement ?? null}
                        icon={<Building2 className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.monthlySalary',
                          'Monthly salary'
                        )}
                        value={monthlySalary}
                        icon={<Euro className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.hourlySalary',
                          'Hourly salary'
                        )}
                        value={hourlySalary}
                        icon={<Euro className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.weeklyHours',
                          'Weekly hours'
                        )}
                        value={weeklyHours}
                        icon={<Clock className="h-4 w-4" />}
                      />
                      <InfoField
                        label={t(
                          'employees.contract.monthlyHours',
                          'Monthly hours'
                        )}
                        value={monthlyHours}
                        icon={<Clock className="h-4 w-4" />}
                      />
                    </div>

                    {detail?.schedule?.dailyHours && (
                      <div className="mt-4 rounded-lg bg-neutral-50 p-4">
                        <p className="mb-2 text-sm font-semibold text-neutral-700">
                          {t('employees.contract.dailyHours', 'Daily hours')}
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                          {(
                            [
                              ['monday', t('common.days.monday', 'Mon')],
                              ['tuesday', t('common.days.tuesday', 'Tue')],
                              ['wednesday', t('common.days.wednesday', 'Wed')],
                              ['thursday', t('common.days.thursday', 'Thu')],
                              ['friday', t('common.days.friday', 'Fri')],
                              ['saturday', t('common.days.saturday', 'Sat')],
                              ['sunday', t('common.days.sunday', 'Sun')],
                            ] as const
                          ).map(([dayKey, dayLabel]) => {
                            const hours = detail.schedule?.dailyHours?.[dayKey];
                            if (hours === null || hours === undefined) {
                              return null;
                            }
                            return (
                              <div
                                key={dayKey}
                                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-center"
                              >
                                <p className="text-xs font-medium text-neutral-500">
                                  {dayLabel}
                                </p>
                                <p className="text-sm font-semibold text-neutral-900">
                                  {formatHours(hours)}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {detail && (
                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <InfoField
                          label={t(
                            'employees.contract.partTime',
                            'Part-time employee'
                          )}
                          value={
                            detail.salarieTempsPartiel === null ||
                            detail.salarieTempsPartiel === undefined
                              ? null
                              : detail.salarieTempsPartiel
                                ? t('common.yes', 'Yes')
                                : t('common.no', 'No')
                          }
                        />
                        <InfoField
                          label={t(
                            'employees.contract.forfaitJour',
                            'Forfait jour'
                          )}
                          value={
                            detail.forfaitJour === null ||
                            detail.forfaitJour === undefined
                              ? null
                              : detail.forfaitJour
                                ? t('common.yes', 'Yes')
                                : t('common.no', 'No')
                          }
                        />
                      </div>
                    )}

                    {detail?.termination && (
                      <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                        <p className="mb-3 text-sm font-semibold text-neutral-700">
                          {t(
                            'employees.contract.termination',
                            'Termination details'
                          )}
                        </p>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <InfoField
                            label={t(
                              'employees.contract.terminationDate',
                              'Exit date'
                            )}
                            value={terminationExitDate}
                            icon={<Calendar className="h-4 w-4" />}
                          />
                          <InfoField
                            label={t(
                              'employees.contract.terminationLastWorkedDay',
                              'Last worked day'
                            )}
                            value={terminationLastWorkedDay}
                            icon={<Calendar className="h-4 w-4" />}
                          />
                          <InfoField
                            label={t(
                              'employees.contract.terminationNotification',
                              'Notification date'
                            )}
                            value={terminationNotificationDate}
                            icon={<Calendar className="h-4 w-4" />}
                          />
                          <InfoField
                            label={t(
                              'employees.contract.terminationMotive',
                              'Motive'
                            )}
                            value={detail.termination?.motive ?? null}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-12 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-200">
                <Briefcase className="h-8 w-8 text-neutral-500" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                {t('employees.contract.notFound', 'No contracts found')}
              </h3>
              <p className="text-sm text-neutral-600">
                {t(
                  'employees.contract.notFoundDescription',
                  'Contracts will appear here once they are available.'
                )}
              </p>
            </div>
          )}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <DocumentsView
              context="employee"
              contextUuid={employeeUuid || ''}
              contextName={fullName}
            />
          </div>
        </TabsContent>

        {/* Bulletins */}
        <TabsContent value="bulletins">
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <Download className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-neutral-900">
              {t('employees.bulletins.downloadTitle', 'Bulletins de paie')}
            </h3>
            <p className="mb-4 text-sm text-neutral-600">
              {t(
                'employees.bulletins.downloadDescription',
                'Telechargez les bulletins de paie depuis OpenPaye pour ce dossier.'
              )}
            </p>
            {totalBulletins > 0 && (
              <p className="mb-4 text-xs text-neutral-500">
                {t('employees.bulletins.metadata', 'Recorded payslips')}:{' '}
                {totalBulletins}
              </p>
            )}
            <button
              onClick={() => setIsDownloadBulletinsDrawerOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
            >
              <Download className="h-4 w-4" />
              {t('employees.bulletins.downloadButton', 'Telecharger les bulletins')}
            </button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Employee Vault Modal */}
      {employeeUuid && (
        <CreateEmployeeVaultModal
          isOpen={isCreateVaultModalOpen}
          onClose={() => setIsCreateVaultModalOpen(false)}
          employeeId={employeeUuid}
          employeeName={fullName}
        />
      )}

      {/* Download Bulletins Drawer */}
      {folderUuid && (
        <DownloadBulletinsDrawer
          isOpen={isDownloadBulletinsDrawerOpen}
          onClose={() => setIsDownloadBulletinsDrawerOpen(false)}
          folderUuid={folderUuid}
          folderName={employee?.nom ? `${employee.prenom} ${employee.nom}` : undefined}
        />
      )}
    </div>
  );
}

function InfoField({
  label,
  value,
  icon,
}: {
  label: string;
  value?: string | null;
  icon?: React.ReactNode;
}) {
  if (!value) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 rounded-lg bg-neutral-50 p-3">
      {icon && <div className="text-neutral-400">{icon}</div>}
      <div className="flex-1">
        <p className="text-xs text-neutral-500">{label}</p>
        <p className="font-medium text-neutral-900">{value}</p>
      </div>
    </div>
  );
}
